/*
 * cx-squad-customiser.js -- controller for snippets/cx-squad-customiser.liquid
 * (Bride Squad / Groom Squad name patches).
 *
 * - Builds the phrase / colourway / couple-title pills from window.CXSquad.
 * - Keeps a live preview grid: one tile per name (+ the couple patch).
 * - Add to cart: renders each patch at print resolution (300 dpi at the
 *   chosen size, min 1200px wide), then adds ONE cart line per patch via
 *   sequential multipart /cart/add.js calls, each with its own
 *   `Custom Image` file. If a call fails midway, the names already added
 *   are removed from the form so a retry only adds what is missing.
 * Self-contained: does not load or depend on custom-personalization-multi-add.js.
 */
(function () {
  'use strict';

  var MAX_NAMES = 20;
  var MAX_LEN = 14;
  var NAME_ALLOWED = /[^A-Za-z .'’&-]/g;
  var PREVIEW_PX = 560;

  function init(root) {
    if (root.dataset.cxInit) return;
    root.dataset.cxInit = '1';
    var S = window.CXSquad;
    var side = root.dataset.side === 'groom' ? 'groom' : 'bride';
    var sideWord = side === 'groom' ? 'Groom' : 'Bride';
    var phrases = S.PHRASES[side];
    var titles = S.COUPLE_TITLES[side];
    var uid = root.dataset.productId || 'x';

    var preview = root.querySelector('[data-cx-preview]');
    var disclaimer = root.querySelector('[data-cx-disclaimer]');
    var namesWrap = root.querySelector('[data-cx-names]');
    var addNameBtn = root.querySelector('[data-cx-add-name]');
    var nameWarn = root.querySelector('[data-cx-name-warn]');
    var skipBtn = root.querySelector('[data-cx-skip-to-couple]');
    var coupleStep = root.querySelector('[data-cx-step="couple"]');
    var sizeStep = root.querySelector('[data-cx-step="size"]');
    var coupleToggle = root.querySelector('[data-cx-couple-toggle]');
    var coupleFields = root.querySelector('[data-cx-couple-fields]');
    var coupleName = root.querySelector('[data-cx-couple-name]');
    var summary = root.querySelector('[data-cx-summary]');
    var errorWrap = root.querySelector('[data-cx-error]');
    var errorText = root.querySelector('[data-cx-error-text]');
    var submit = root.querySelector('[data-cx-submit]');
    var submitText = root.querySelector('[data-cx-submit-text]');
    var progress = root.querySelector('[data-cx-progress]');
    var removeIconTpl = root.querySelector('template[data-cx-remove-icon]');
    var moneyFormat = root.dataset.moneyFormat || '₹{{amount_no_decimals}}';

    var state = { phrase: 0, colourway: S.COLOURWAYS[0].id, title: 0, revealed: false, busy: false };

    // ---------- pills ----------
    function pillGroup(container, name, items, labelFn, onPick, checkedIdx) {
      items.forEach(function (item, i) {
        var id = 'Cx-' + name + '-' + uid + '-' + i;
        var input = document.createElement('input');
        input.type = 'radio';
        input.name = 'cx-' + name + '-' + uid;
        input.id = id;
        input.value = String(i);
        if (i === checkedIdx) input.checked = true;
        input.addEventListener('change', function () {
          if (input.checked) onPick(i);
        });
        var label = document.createElement('label');
        label.setAttribute('for', id);
        labelFn(label, item);
        container.appendChild(input);
        container.appendChild(label);
      });
    }
    pillGroup(
      root.querySelector('[data-cx-phrases]'),
      'phrase',
      phrases,
      function (label, p) {
        label.textContent = p.label;
      },
      function (i) {
        state.phrase = i;
        refresh();
      },
      0
    );
    pillGroup(
      root.querySelector('[data-cx-colourways]'),
      'colour',
      S.COLOURWAYS,
      function (label, c) {
        var sw = document.createElement('span');
        sw.className = 'cx-squad__swatch';
        sw.style.background = c.swatch;
        label.appendChild(sw);
        label.appendChild(document.createTextNode(c.label));
      },
      function (i) {
        state.colourway = S.COLOURWAYS[i].id;
        refresh();
      },
      0
    );
    pillGroup(
      root.querySelector('[data-cx-couple-titles]'),
      'title',
      titles,
      function (label, t) {
        label.textContent = t.label;
      },
      function (i) {
        state.title = i;
        refresh();
      },
      0
    );

    // ---------- name rows ----------
    function nameInputs() {
      return Array.prototype.slice.call(namesWrap.querySelectorAll('[data-cx-name]'));
    }

    function cleanName(v) {
      return v.replace(/\s+/g, ' ').trim();
    }

    function sanitize(input) {
      var before = input.value;
      var after = before.replace(NAME_ALLOWED, '');
      if (after.length > MAX_LEN) after = after.slice(0, MAX_LEN);
      if (after !== before) {
        input.value = after;
        nameWarn.textContent = 'Only English letters, spaces and . ’ - & can be printed.';
        nameWarn.hidden = false;
        clearTimeout(warnTimer);
        warnTimer = setTimeout(function () {
          nameWarn.hidden = true;
        }, 6000);
      }
    }
    var warnTimer = null;

    function renumber() {
      var rows = namesWrap.querySelectorAll('.cx-squad__name-row');
      rows.forEach(function (row, i) {
        row.querySelector('.cx-squad__name-index').textContent = i + 1 + '.';
        var inp = row.querySelector('[data-cx-name]');
        inp.setAttribute('aria-label', 'Name ' + (i + 1));
        var rm = row.querySelector('[data-cx-remove]');
        rm.hidden = rows.length === 1 && !inp.value;
      });
      addNameBtn.disabled = rows.length >= MAX_NAMES;
    }

    function addRow(value, focus) {
      if (nameInputs().length >= MAX_NAMES) return null;
      var row = document.createElement('div');
      row.className = 'cx-squad__name-row';
      var idx = document.createElement('span');
      idx.className = 'cx-squad__name-index';
      var wrap = document.createElement('div');
      wrap.className = 'cx-squad__input-wrap';
      var input = document.createElement('input');
      input.type = 'text';
      input.className = 'cx-squad__input';
      input.maxLength = MAX_LEN;
      input.autocomplete = 'off';
      input.setAttribute('autocapitalize', 'words');
      input.placeholder = 'Type a name';
      input.setAttribute('data-cx-name', '');
      input.value = value || '';
      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'cx-squad__remove';
      rm.setAttribute('data-cx-remove', '');
      rm.setAttribute('aria-label', 'Remove this name');
      if (removeIconTpl) rm.innerHTML = removeIconTpl.innerHTML;
      else rm.textContent = '×';
      wrap.appendChild(input);
      wrap.appendChild(rm);
      row.appendChild(idx);
      row.appendChild(wrap);
      namesWrap.appendChild(row);

      input.addEventListener('input', function () {
        sanitize(input);
        renumber();
        refresh();
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          var inputs = nameInputs();
          var next = inputs[inputs.indexOf(input) + 1];
          if (next) next.focus();
          else if (cleanName(input.value)) addRow('', true);
        }
      });
      rm.addEventListener('click', function () {
        nameWarn.hidden = true;
        if (nameInputs().length === 1) {
          input.value = '';
        } else {
          row.remove();
        }
        renumber();
        refresh();
      });
      renumber();
      if (focus) input.focus();
      return input;
    }

    addNameBtn.addEventListener('click', function () {
      var empty = nameInputs().filter(function (i) {
        return !cleanName(i.value);
      })[0];
      if (empty) {
        empty.focus();
        return;
      }
      addRow('', true);
    });
    skipBtn.addEventListener('click', function () {
      state.revealed = true;
      coupleToggle.checked = true;
      refresh();
      coupleStep.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    coupleToggle.addEventListener('change', refresh);
    coupleName.addEventListener('input', function () {
      sanitize(coupleName);
      refresh();
    });
    root.querySelectorAll('[data-cx-size-radio]').forEach(function (r) {
      r.addEventListener('change', refresh);
    });

    // ---------- model ----------
    function patches() {
      var list = [];
      var phrase = phrases[state.phrase];
      nameInputs().forEach(function (inp) {
        var n = cleanName(inp.value);
        if (n) list.push({ kind: 'squad', lockup: phrase, name: n, input: inp });
      });
      if (coupleToggle.checked) {
        list.push({ kind: 'couple', lockup: titles[state.title], name: cleanName(coupleName.value) });
      }
      return list;
    }

    function selectedSize() {
      var r = root.querySelector('[data-cx-size-radio]:checked');
      if (!r) return null;
      return { variantId: r.value, inches: parseFloat(r.dataset.sizeIn), cents: parseInt(r.dataset.priceCents, 10) };
    }

    function formatMoney(cents) {
      var amount = cents / 100;
      var noDec = Math.round(amount).toLocaleString('en-IN');
      var dec = amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return moneyFormat
        .replace(/\{\{\s*amount_no_decimals[^}]*\}\}/, noDec)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, dec)
        .replace(/\{\{\s*amount\s*\}\}/, dec)
        .replace(/<[^>]*>/g, '');
    }

    // ---------- preview ----------
    var tileCache = [];
    function tileKey(p) {
      return [p.kind, p.lockup.label, p.name, state.colourway].join('|');
    }

    function renderPreview(list) {
      var shown = list.length
        ? list
        : [{ kind: 'squad', lockup: phrases[state.phrase], name: 'Your Name', placeholder: true }];
      preview.dataset.count = String(shown.length);
      var oldTiles = tileCache;
      tileCache = [];
      var frag = document.createDocumentFragment();
      shown.forEach(function (p) {
        var key = tileKey(p) + (p.placeholder ? '|ph' : '');
        var reuse = null;
        for (var i = 0; i < oldTiles.length; i++) {
          if (oldTiles[i] && oldTiles[i].key === key) {
            reuse = oldTiles[i];
            oldTiles[i] = null;
            break;
          }
        }
        if (!reuse) {
          var fig = document.createElement('figure');
          fig.className = 'cx-squad__tile';
          var cv = document.createElement('canvas');
          S.render(cv, {
            lockup: p.lockup,
            name: p.name,
            colourway: state.colourway,
            special: p.kind === 'couple',
            widthPx: PREVIEW_PX,
            placeholder: !!p.placeholder,
          });
          cv.setAttribute('role', 'img');
          cv.setAttribute('aria-label', p.lockup.label + (p.name ? ' patch for ' + p.name : ' patch'));
          var cap = document.createElement('figcaption');
          cap.textContent = p.placeholder
            ? 'Sample. Type a name below to see your patches.'
            : p.kind === 'couple'
            ? sideWord + '’s patch'
            : p.name;
          fig.appendChild(cv);
          fig.appendChild(cap);
          reuse = { key: key, el: fig };
        }
        tileCache.push(reuse);
        frag.appendChild(reuse.el);
      });
      preview.innerHTML = '';
      preview.appendChild(frag);
      disclaimer.hidden = list.length === 0;
    }

    // ---------- refresh ----------
    var refreshTimer = null;
    function refresh() {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(doRefresh, 60);
    }
    function doRefresh() {
      var list = patches();
      var squadCount = list.filter(function (p) {
        return p.kind === 'squad';
      }).length;
      if (squadCount > 0) state.revealed = true;
      coupleStep.hidden = !state.revealed;
      sizeStep.hidden = !state.revealed;
      coupleFields.hidden = !coupleToggle.checked;
      renderPreview(list);

      var size = selectedSize();
      var n = list.length;
      if (n && size && state.revealed) {
        summary.hidden = false;
        summary.textContent =
          n + (n === 1 ? ' patch' : ' patches') + ' × ' + formatMoney(size.cents) + ' = ' + formatMoney(size.cents * n);
      } else {
        summary.hidden = true;
      }
      if (!state.busy) {
        submit.disabled = !(n && size);
        submitText.textContent = n ? 'Add ' + n + (n === 1 ? ' patch' : ' patches') + ' to cart' : 'Add to cart';
      }
    }

    // ---------- cart ----------
    function showError(msg) {
      errorText.textContent = msg;
      errorWrap.hidden = false;
    }
    function clearError() {
      errorWrap.hidden = true;
      errorText.textContent = '';
    }
    function setBusy(busy) {
      state.busy = busy;
      submit.disabled = busy;
      submit.toggleAttribute('aria-disabled', busy);
      submit.classList.toggle('loading', busy);
      var spinner = submit.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !busy);
      if (!busy) progress.hidden = true;
    }

    function slug(s) {
      return (
        s
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '') || 'patch'
      );
    }

    function printWidthPx(inches) {
      return Math.max(1200, Math.round(inches * 300));
    }

    function renderFile(p, widthPx) {
      var cv = document.createElement('canvas');
      S.render(cv, {
        lockup: p.lockup,
        name: p.name,
        colourway: state.colourway,
        special: p.kind === 'couple',
        widthPx: widthPx,
      });
      return S.toBlob(cv).then(function (blob) {
        if (!blob) throw new Error('Could not create your patch image. Please try again.');
        var fname = slug(p.lockup.label) + '-' + slug(p.name || 'patch') + '.png';
        return new File([blob], fname, { type: 'image/png' });
      });
    }

    function colourwayLabel() {
      for (var i = 0; i < S.COLOURWAYS.length; i++) if (S.COLOURWAYS[i].id === state.colourway) return S.COLOURWAYS[i].label;
      return state.colourway;
    }

    function addOne(p, size, setId, withSections) {
      var widthPx = printWidthPx(size.inches);
      return renderFile(p, widthPx).then(function (file) {
        var fd = new FormData();
        fd.append('id', size.variantId);
        fd.append('quantity', '1');
        fd.append('properties[Patch]', p.kind === 'couple' ? sideWord + '’s patch' : 'Squad name patch');
        fd.append('properties[Phrase]', p.lockup.label);
        if (p.name) fd.append('properties[Name]', p.name);
        fd.append('properties[Colourway]', colourwayLabel());
        fd.append('properties[Custom Image]', file);
        fd.append('properties[_Squad Set]', setId);
        if (withSections) {
          fd.append('sections', 'cart-drawer,cart-icon-bubble');
          fd.append('sections_url', window.location.pathname);
        }
        var url = (window.routes && window.routes.cart_add_url) || '/cart/add';
        // Shopflo (app embed) watches every /cart/add call and opens its
        // cart, or on some setups redirects to /cart, unless the URL carries
        // skip-cart-open=true. Without this the page could navigate away
        // after the FIRST name and the rest of the squad would never be
        // added. Only the final call lets Shopflo open the cart.
        return fetch(url + '.js' + (withSections ? '' : '?skip-cart-open=true'), {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: fd,
        })
          .then(function (res) {
            return res.json();
          })
          .then(function (json) {
            if (json.status) throw new Error(json.description || json.message || 'Could not add to cart.');
            return json;
          });
      });
    }

    function renderCartSections(response) {
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart && typeof cart.renderContents === 'function' && response && response.sections) {
        cart.renderContents(response);
      }
      var drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.remove('is-empty');
      var drawerItems = document.querySelector('cart-drawer-items');
      if (drawerItems) drawerItems.classList.remove('is-empty');
    }

    function resetForm() {
      namesWrap.innerHTML = '';
      addRow('', false);
      coupleToggle.checked = false;
      coupleName.value = '';
      nameWarn.hidden = true;
      state.revealed = false;
      refresh();
    }

    submit.addEventListener('click', function () {
      if (state.busy) return;
      clearError();
      var list = patches();
      var size = selectedSize();
      if (!list.length) return showError('Please type at least one name.');
      if (!size) return showError('Please choose a size.');

      var setId = 'SQ-' + Date.now().toString(36).toUpperCase();
      var total = list.length;
      var done = [];
      var lastResponse = null;
      setBusy(true);
      progress.hidden = false;

      var chain = S.ready();
      list.forEach(function (p, i) {
        chain = chain.then(function () {
          progress.textContent = 'Adding patch ' + (i + 1) + ' of ' + total + '…';
          return addOne(p, size, setId, i === total - 1).then(function (resp) {
            done.push(p);
            lastResponse = resp;
          });
        });
      });

      chain
        .then(function () {
          renderCartSections(lastResponse);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cx-squad', productVariantId: size.variantId, cartData: lastResponse });
          }
          setBusy(false);
          resetForm();
        })
        .catch(function (err) {
          // Drop whatever already made it into the cart so a retry only
          // adds the missing patches.
          done.forEach(function (p) {
            if (p.kind === 'couple') {
              coupleToggle.checked = false;
              coupleName.value = '';
            } else if (p.input) {
              var row = p.input.closest('.cx-squad__name-row');
              if (row && nameInputs().length > 1) row.remove();
              else p.input.value = '';
            }
          });
          renumber();
          if (done.length) {
            // Show the patches that did make it into the cart.
            fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble')
              .then(function (res) {
                return res.json();
              })
              .then(function (sections) {
                renderCartSections({ sections: sections, id: size.variantId });
              })
              .catch(function () {});
          }
          setBusy(false);
          showError(
            (done.length ? done.length + ' of ' + total + ' patches were added. ' : '') +
              (err && err.message ? err.message.replace(/\.?\s*$/, '.') : 'Something went wrong.') +
              (done.length ? ' Please try again to add the rest.' : ' Please try again.')
          );
          refresh();
        });
    });

    addRow('', false);
    S.ready().then(function () {
      tileCache = []; // tiles drawn before the fonts loaded used fallbacks
      refresh();
    });
    refresh();
  }

  function boot() {
    if (!window.CXSquad) return;
    document.querySelectorAll('[data-cx-squad]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
